// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC.

<template>
  <v-container fluid grid-list-md>
    <v-row wrap>
      <v-col cols="12" justify-center>
        <v-card>
          <v-card-text>
            <v-container fluid style="padding: 0px;">
              <v-row wrap>
                <v-col cols="12" md="3">
                  <date-time-view
                  v-model="dateStart"
                  :allowed-dates="allowedDates"
                  label="Start Date"
                  ></date-time-view>
                </v-col>
                <v-col cols="12" md="3">
                  <date-time-view
                  v-model="dateEnd"
                  :allowed-dates="allowedDates"
                  label="End Date"
                  ></date-time-view>
                </v-col>
              </v-row>
              <v-row wrap>
                <v-col cols="12" md="auto">
                  <v-btn dark color="blue darken-1" class="mr-1" @click="resetDate()">Today</v-btn>
                  <v-btn dark color="blue darken-2" class="mr-1" @click="resetWeek()">Last 7 Days</v-btn>
                  <v-btn dark color="blue darken-3" class="mr-1" @click="resetMonth()">Last 30 Days</v-btn>
                  <v-btn v-if="showExpButton" class="mx-1" @click="showHideAll()">{{ showHideText }}</v-btn>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="12" md="auto">
                  <v-btn class="mx-1" color="green" dark :href="downloadUrl" download>Download XLSX</v-btn>
                </v-col>
              </v-row>
              <v-row wrap>
                <v-col cols="12" md="auto">
                  <span class="mx-1">Total Events: {{ events.length }}</span>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text style="padding: 0px;">
            <v-progress-linear :indeterminate="true" v-if="loading"></v-progress-linear>
            <div style="padding: 12px; padding-left: 24px; padding-right: 24px; border-bottom: 1px solid rgba(0, 0, 0, 0.82);">
              <v-row wrap>
                <v-col cols="12" md="auto" class="font-weight-bold">
                  Total Tags: {{ eventsByWorker.length }}
                </v-col>
                <v-col cols="12" md="auto" class="font-weight-bold">
                  Report Total Hours: {{ durationFormat(totalHours) }}
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="12" md="auto" class="font-weight-bold">
                  Start Date: {{ dateFormat(dateStart) }}
                </v-col>
                <v-col cols="12" md="auto" class="font-weight-bold">
                  End Date: {{ dateFormat(dateEnd) }}
                </v-col>
              </v-row>
            </div>
            <v-expansion-panels  v-model="panel" multiple>
              <v-expansion-panel v-for="(worker, i) in eventsByWorker" :key="i" expand>
                <v-expansion-panel-header style="border-top: 1px solid rgba(0, 0, 0, 0.92); border-radius:0px;">
                  <v-row wrap>
                    <v-col cols="12" md="6" class="font-weight-bold">
                      {{ worker.name }} - ({{ worker.serial }}) - {{ worker.tag }}
                    </v-col>
                    <v-col cols="12" md="6" class="font-weight-bold">
                      Total Hours: {{ durationFormat(worker.duration) }}
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card style="border: 1px solid rgba(0, 0, 0, 0.4);">
                    <v-card-text>
                      <template v-for="(day, j) in worker.days">
                        <v-row wrap :key="1000 + (i * 1000) + j" style="border-top: 1px solid rgba(0, 0, 0, 0.82);" dense>
                          <v-col cols="12" md="4" class="font-weight-bold">
                            {{ day.date }}
                          </v-col>
                          <v-col cols="12" md="4" class="font-weight-bold">
                            Day Total: {{ durationFormat(day.duration) }}
                          </v-col>
                        </v-row>
                        <template v-for="(event, k) in day.events">
                          <v-row wrap :key="10000 + k + (j * 100) + (i * 1000)" style="border-top: 1px solid rgba(0, 0, 0, 0.12);" dense>
                            <v-col cols="12" md="4">
                              {{ eventTimeOnly(event) }}
                            </v-col>
                            <v-col cols="12" md="8">
                              {{ getZoneName(event.zone) || 'None' }} - {{ getEndpointName(event.endpoint) }} - {{ event.direction }}
                            </v-col>
                          </v-row>
                        </template>
                      </template>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import ApiRequests from '@/api/ApiRequests'
import {DATEFORMATSTRING, ENDPOINT} from '@/shared/constants'
import DateTimeView from '@/components/DateTimeView'

import {LookupMixin} from '@/shared/mixins'

export default {
  components: {
    DateTimeView
  },

  mixins: [LookupMixin],

  data () {
    return {
      dateStart: moment().startOf('day').format(),
      dateEnd: moment().endOf('day').format(),
      events: [],
      eventsByWorker: [],
      endpoints: [],
      loading: false,
      panel: [],
      rowsPerPageItems: [4, 8, 12, -1],
      pagination: {
        rowsPerPage: 4
      },
      showExpButton: false,
      things: [],
      totalHours: 0,
      zones: []
    }
  },

  created () {
    Promise.all([
      ApiRequests.getEndpoints().then(r => {
        this.endpoints = r.data
        return r
      }),
      ApiRequests.getThings().then(r => {
        this.things = r.data
        return r
      }),
      ApiRequests.getZones().then(r => {
        this.zones = r.data
        return r
      })
    ]).then(r => {
      this.getEvents()
    })
  },

  computed: {
    ...mapGetters([
      'selectedSite',
      'user'
    ]),

    showHideText: function () {
      if (this.panel.length > 0) {
        return 'Collapse All'
      }
      return 'Expand All'
    },

    downloadUrl: function () {
      var params = {
        site: this.selectedSite._id
      }

      if (this.dateStart) {
        params.after = this.dateStart
      }
      if (this.dateEnd) {
        params.before = this.dateEnd
      }
      var url = ENDPOINT + 'eventsbything/export?' + new URLSearchParams(params).toString()
      return url
    }
  },

  watch: {
    dateEnd: function () {
      this.getEvents()
    },

    dateStart: function () {
      this.getEvents()
    },

    selectedSite: function () {
      this.getEvents()
    },

    eventsByWorker: function () {
      if (this.eventsByWorker.length > 0) {
        this.showExpButton = true
        return
      }
      this.showExpButton = false
    }
  },

  methods: {
    allowedDates: function (date) {
      // for some reason the date sent by the picker is 12 noon.
      var mdate = moment(date)
      return mdate.isBefore(moment()) || mdate.isSame(new Date(), 'day')
    },

    compare: function (a, b) {
      var aName = a.name.toUpperCase()
      var bName = b.name.toUpperCase()
      if (aName < bName) {
        return -1
      } else if (bName < aName) {
        return 1
      }
      return 0
    },

    dateFormat: function (date) {
      return moment(date).format(DATEFORMATSTRING)
    },

    durationFormat: function (seconds) {
      var duration = moment.duration(seconds)
      return Math.floor(duration.as('hours')) + ':' + this.pad(duration.minutes())
    },

    pad: function (num) {
      if (num < 10) {
        return '0' + num
      }
      return num
    },

    eventDate: function (event) {
      return this.eventTimeMoment(event).format(DATEFORMATSTRING)
    },

    eventTime: function (event) {
      return this.eventTimeMoment(event).format(DATEFORMATSTRING + ' hh:mm:ss a')
    },

    eventTimeOnly: function (event) {
      return this.eventTimeMoment(event).format('hh:mm a')
    },

    eventTimeMoment: function (event) {
      var time = event.time
      if (event.edits && event.edits.length > 0) {
        time = event.edits[event.edits.length - 1].Time
      }
      return moment(time)
    },

    getEvents: function () {
      this.loading = true
      var total = 0
      this.panel = []

      ApiRequests.getEvents(this.selectedSite._id,
      moment(this.dateStart).format(),
      moment(this.dateEnd).format(),
      10000).then(response => {
        this.events = response.data
        var filteredEvents = this.events.filter(e => e.thing !== '')

        var things = {}
        filteredEvents.forEach(element => {
          if (element.thing && element.thing !== '787878787878787878787878') {
            // add to list of things
            if (!things[element.thing]) {
              var t = this.getThing(element.thing)
              var name = 'N/A'
              var serial = 'N/A'
              var tag = 'N/A'
              if (t) {
                name = t.common.name
                serial = t.common.serial
                tag = this.getThingTags(t)
              }

              things[element.thing] = {
                name: name,
                serial: serial,
                tag: tag,
                days: []
              }
            }

            var day = moment(element.time).format(DATEFORMATSTRING)
            if (!things[element.thing].days[day]) {
              things[element.thing].days[day] = {
                events: [],
                date: day
              }
            }
            things[element.thing].days[day].events.push(element)
          }
        })

        var list = []
        for (var i in things) {
          var item = things[i]

          var newList = []
          var totalDuration = 0
          for (var j in item.days) {
            newList.push(item.days[j])
            item.days[j].duration = this.eventTimeMoment(item.days[j].events[0]) - this.eventTimeMoment(item.days[j].events[item.days[j].events.length - 1])
            totalDuration = totalDuration + item.days[j].duration
          }
          item.days = newList
          item.duration = totalDuration
          total = total + totalDuration
          list.push(item)
        }
        list.sort(this.compare)

        this.totalHours = total
        this.eventsByWorker = list
        this.loading = false
      })
    },

    resetDate: function () {
      this.dateStart = moment().startOf('day').format()
      this.dateEnd = moment().endOf('day').format()
    },

    resetWeek: function () {
      this.dateStart = moment().startOf('day').subtract(6, 'days').format()
      this.dateEnd = moment().endOf('day').format()
    },

    resetMonth: function () {
      this.dateStart = moment().startOf('day').subtract(29, 'days').format()
      this.dateEnd = moment().endOf('day').format()
    },

    showHideAll: function () {
      if (this.panel.length > 0) {
        this.panel = []
        return
      }
      this.panel = Array.apply(null, {length: this.eventsByWorker.length}).map(Number.call, Number)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/ReportEvents.vue